import React, { useMemo, useState, useEffect } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
} from "@tanstack/react-table";
import { FaEdit, FaTrash, FaSearch } from "react-icons/fa";
import { sendApiRequest } from '../../utils/api';
import { toast } from "react-toastify";
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { motion } from 'framer-motion';

const TopicListTable = () => {
  const [globalFilter, setGlobalFilter] = useState("");
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [searchFocused, setSearchFocused] = useState(false);

  useEffect(() => {
    fetchTopics();
  }, []);

  const fetchTopics = async () => {
    try {
      const response = await sendApiRequest('api/admin/topics', {}, 'GET');
      console.log(response);
      if (response) {
        setTopics(response);
      } else {
        setTopics([]);
      }
      setLoading(false);
    } catch (error) {
      toast.error('Error fetching topics');
      setError('Failed to load topics');
      setTopics([]);
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Delete Topic',
      text: "Are you sure you want to delete this topic? This action cannot be undone.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ff4444',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
      background: '#fff',
      color: '#333'
    });

    if (result.isConfirmed) {
      try {
        await sendApiRequest(`api/admin/topics/${id}`, {}, 'DELETE');
        Swal.fire({
          title: 'Deleted!',
          text: 'Topic has been deleted.',
          icon: 'success',
          background: '#1c1c1c',
          color: '#fff'
        });
        fetchTopics(); // Refresh the list
      } catch (error) {
        Swal.fire({
          title: 'Error!',
          text: 'Failed to delete topic.',
          icon: 'error',
          background: '#1c1c1c',
          color: '#fff'
        });
      }
    }
  };

  const handleEdit = (id) => {
    navigate(`/admin-panel/topic-edit/${id}`);
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "_id",
        header: "Sr. No.",
        cell: ({ row }) => (
          <span className="font-medium text-gray-800">{row.index + 1}</span>
        ),
        meta: { className: "w-[60px]" },
      },
      {
        accessorKey: "createdAt",
        header: "Date",
        cell: ({ getValue }) => {
          const date = getValue();
          return date ? (
            <span className="text-gray-600">{new Date(date).toLocaleDateString('en-GB', {
              year: 'numeric',
              month: 'short',
              day: 'numeric'
            })}</span>
          ) : '-';
        },
        meta: { className: "w-[100px]" },
      },
      {
        accessorKey: "title",
        header: "Topic",
        cell: ({ getValue }) => (
          <div className="whitespace-normal break-words text-gray-800 font-medium">
            {getValue() || '-'}
          </div>
        ),
        meta: { className: "w-[180px] max-w-[180px]" },
      },
      {
        accessorKey: "isPaid",
        header: "Type",
        cell: ({ getValue }) => (
          <span className={`px-2 py-1 rounded-full text-sm ${
            getValue() ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
          }`}>
            {getValue() ? "Paid" : "Demo"}
          </span>
        ),
        meta: { className: "w-[80px]" },
      },
      {
        accessorKey: "exam.name",
        header: "Exam",
        cell: ({ getValue }) => (
          <div className="whitespace-normal break-words text-gray-700">
            {getValue() || '-'}
          </div>
        ),
        meta: { className: "w-[220px] max-w-[220px]" },
      },
      {
        accessorKey: "wordLength",
        header: "Word Length",
        cell: ({ getValue }) => (
          <span className="text-gray-600">{getValue() || '-'}</span>
        ),
        meta: { className: "w-[80px]" },
      },
      {
        accessorKey: "markingRule.title",
        header: "Marking Rule",
        cell: ({ getValue }) => (
          <div className="whitespace-normal break-words text-gray-700">
            {getValue() || '-'}
          </div>
        ),
        meta: { className: "w-[220px] max-w-[220px]" },
      },
      {
        accessorKey: "wordsPerMin",
        header: "WPM",
        cell: ({ getValue }) => (
          <div className="whitespace-normal break-words text-gray-600">
            {getValue() || '-'}
          </div>
        ),
        meta: { className: "w-[220px] max-w-[220px]" },
      },
      {
        accessorKey: "transcriptionTime",
        header: "Transcription",
        cell: ({ getValue }) => (
          <span className="text-gray-600">
            {getValue() ? `${getValue()} Minutes` : '-'}
          </span>
        ),
        meta: { className: "w-[120px]" },
      },
      {
        id: "action",
        header: "Action",
        cell: ({ row }) => (
          <div className="flex gap-3">
            <motion.button 
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => handleEdit(row.original._id)}
              className="p-2.5 text-blue-600 hover:text-white hover:bg-blue-600 rounded-lg transition-all duration-300 border-2 border-blue-600 hover:shadow-lg"
            >
              <FaEdit className="w-4 h-4" />
            </motion.button>
            <motion.button 
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => handleDelete(row.original._id)}
              className="p-2.5 text-red-600 hover:text-white hover:bg-red-600 rounded-lg transition-all duration-300 border-2 border-red-600 hover:shadow-lg"
            >
              <FaTrash className="w-4 h-4" />
            </motion.button>
          </div>
        ),
        meta: { className: "w-[120px]" },
      }
    ],
    []
  );

  const table = useReactTable({
    data: topics || [],
    columns,
    state: {
      globalFilter,
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  if (loading) {
    return (
      <div className="p-8 bg-white rounded-2xl shadow-xl border border-gray-200">
        <div className="flex items-center justify-center space-x-2">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <span className="text-gray-600">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 bg-white rounded-2xl shadow-xl border border-gray-200">
        <div className="text-red-500 text-center">
          <svg className="w-6 h-6 inline-block mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          {error}
        </div>
      </div>
    );
  }

  if (!topics || topics.length === 0) {
    return (
      <div className="p-8 bg-white rounded-2xl shadow-xl border border-gray-200">
        <div className="text-gray-500 text-center">
          <svg className="w-12 h-12 inline-block mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
          </svg>
          <p className="mt-2">No topics found</p>
          <p className="text-sm text-gray-400">Create a new topic to get started</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 bg-white rounded-2xl shadow-xl border border-gray-200">
      <div className="mb-8 flex justify-between items-center">
        <h2 className="text-3xl font-bold text-gray-800 tracking-tight">
          Topic List
          <div className="h-1 w-20 bg-blue-500 mt-2 rounded-full"></div>
        </h2>
        <div className="relative group">
          <input
            className={`pl-12 pr-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:outline-none transition-all duration-300 w-72 ${
              searchFocused ? 'shadow-md' : 'group-hover:shadow-md'
            }`}
            placeholder="Search topics..."
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
          />
          <div className="absolute left-4 top-3.5 flex items-center">
            <FaSearch className="w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-colors duration-300" />
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200 shadow-lg">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className="border-b border-gray-200">
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className={`px-6 py-4 font-medium text-gray-600 bg-gray-50 ${
                        header.column.columnDef.meta?.className || ""
                      }`}>
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="bg-white">
              {table.getRowModel().rows.map((row, index) => (
                <tr 
                  key={row.id} 
                  className={`hover:bg-gray-50 transition-colors duration-200 cursor-pointer ${
                    index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                  }`}>
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className={`px-6 py-4 ${
                        cell.column.columnDef.meta?.className || ""
                      }`}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex justify-between items-center mt-8">
        <div>
          <select
            className="px-4 py-2.5 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:outline-none transition-all duration-300 hover:shadow-md cursor-pointer bg-white text-gray-700"
            value={table.getState().pagination.pageSize}
            onChange={(e) => table.setPageSize(Number(e.target.value))}
          >
            {[10, 25, 50].map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                Show {pageSize} entries
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-4">
          <button
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
            className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border-2 border-gray-300 rounded-xl hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 hover:shadow-md"
          >
            Previous
          </button>
          <span className="text-sm font-medium text-gray-600">
            Page {table.getState().pagination.pageIndex + 1} of{" "}
            {table.getPageCount()}
          </span>
          <button
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
            className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border-2 border-gray-300 rounded-xl hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 hover:shadow-md"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default TopicListTable;
